import type { AgentMessage, SocketClosed } from '../types'

export interface AgentSocketHandlers {
  onOpen?: () => void
  onMessage?: (msg: AgentMessage) => void
  /** Raw s16le PCM at 16 kHz. The server sends fixed 1280-byte (40 ms) chunks. */
  onAudio?: (pcm: ArrayBuffer) => void
  onClose?: (closed: SocketClosed) => void
}

export interface AgentSocket {
  /** Binary out: raw s16le PCM. Dropped silently unless the socket is open. */
  sendAudio: (pcm: ArrayBufferLike) => void
  /** The only text message the agent accepts. */
  interrupt: () => void
  close: () => void
}

/**
 * One connection to the voice agent.
 *
 * Binary frames are audio, text frames are JSON — the browser tells them apart
 * by frame type, which is the whole framing protocol.
 *
 */
export function openAgentSocket(url: string, handlers: AgentSocketHandlers): AgentSocket {
  const ws = new WebSocket(url)
  ws.binaryType = 'arraybuffer'

  // One close callback per socket, however it ends. Both an error and a close
  // can fire for the same failure.
  let closed = false
  function closeOnce(code: number, reason: string): void {
    if (closed) return
    closed = true
    handlers.onClose?.({ code, reason })
  }

  ws.onopen = () => handlers.onOpen?.()

  ws.onmessage = (e: MessageEvent) => {
    if (e.data instanceof ArrayBuffer) {
      handlers.onAudio?.(e.data)
      return
    }
    try {
      // Unknown types are ignored rather than thrown on: the agent may grow
      // new ones (bot_started/bot_stopped are specified.
      handlers.onMessage?.(JSON.parse(String(e.data)) as AgentMessage)
    } catch {
      // A malformed text frame is not worth killing a conversation over.
    }
  }

  // 1006 with no reason is what a browser reports for an abnormal close —
  // there is no close frame to read in that case.
  ws.onerror = () => closeOnce(1006, '')
  ws.onclose = (e: CloseEvent) => closeOnce(e.code, e.reason)

  return {
    sendAudio(pcm) {
      if (ws.readyState === WebSocket.OPEN) ws.send(pcm as ArrayBuffer)
    },
    interrupt() {
      if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'interrupt' }))
    },
    close() {
      closed = true // a close we asked for is not an event to report back
      try {
        ws.close()
      } catch {
        // Already closing; nothing to do.
      }
    },
  }
}
